// tslint:disable: no-any
import { AxiosResponse } from "axios"
import { Dispatch } from "redux"

import API from "../../Api"
import CommonHelper from "../../helpers/CommonHelper"
import AuthHelper from "../../helpers/AuthHelper"
import { OrganizationSurveyType } from "../../types/OrganizationSurveyType"

export interface CreateOrganizationSurveyParams {
  surveyId: string
  organizationId: string
  callListQueueId?: string | null
}

export interface UpdateOrganizationSurveyParams {
  surveyId?: string
  organizationId?: string
  callListQueueId?: string | null
}

export interface FetchOrganizationSurveysFilter {
  surveyId?: string
  organizationId?: string
  callListQueueId?: string | null
  page?: number
  limit?: number
}

type FetchOrganizationSurveys = (filter?: FetchOrganizationSurveysFilter) => (dispatch: Dispatch) => void
type FetchOrganizationSurvey = (id: string) => (dispatch: Dispatch) => void
type FetchOrganizationSurveysByOrganizationId = (organizationId: string) => (dispatch: Dispatch) => void
type FetchOrganizationSurveysByCallListQueueId = (callListQueueId: string) => (dispatch: Dispatch) => void
type CreateOrganizationSurvey = (values: CreateOrganizationSurveyParams) => (dispatch: Dispatch) => void
type UpdateOrganizationSurvey = (id: string, values: UpdateOrganizationSurveyParams) => (dispatch: Dispatch) => void
type DeleteOrganizationSurvey = (id: string) => (dispatch: Dispatch) => void
type ClearOrganizationSurveys = () => void

export interface OrganizationSurveysActions {
  fetchOrganizationSurveys: FetchOrganizationSurveys
  fetchOrganizationSurvey: FetchOrganizationSurvey
  fetchOrganizationSurveysByOrganizationId: FetchOrganizationSurveysByOrganizationId
  fetchOrganizationSurveysByCallListQueueId: FetchOrganizationSurveysByCallListQueueId
  createOrganizationSurvey: CreateOrganizationSurvey
  updateOrganizationSurvey: UpdateOrganizationSurvey
  deleteOrganizationSurvey: DeleteOrganizationSurvey
  clearOrganizationSurveys: ClearOrganizationSurveys
}

class OrganizationSurveys {
  public static FETCH_ORGANIZATION_SURVEYS_TRIGGER = "FETCH_ORGANIZATION_SURVEYS_TRIGGER"
  public static FETCH_ORGANIZATION_SURVEYS_SUCCESS = "FETCH_ORGANIZATION_SURVEYS_SUCCESS"
  public static FETCH_ORGANIZATION_SURVEYS_ERROR = "FETCH_ORGANIZATION_SURVEYS_ERROR"

  public static FETCH_ORGANIZATION_SURVEY_TRIGGER = "FETCH_ORGANIZATION_SURVEY_TRIGGER"
  public static FETCH_ORGANIZATION_SURVEY_SUCCESS = "FETCH_ORGANIZATION_SURVEY_SUCCESS"
  public static FETCH_ORGANIZATION_SURVEY_ERROR = "FETCH_ORGANIZATION_SURVEY_ERROR"

  public static CREATE_ORGANIZATION_SURVEY_TRIGGER = "CREATE_ORGANIZATION_SURVEY_TRIGGER"
  public static CREATE_ORGANIZATION_SURVEY_SUCCESS = "CREATE_ORGANIZATION_SURVEY_SUCCESS"
  public static CREATE_ORGANIZATION_SURVEY_ERROR = "CREATE_ORGANIZATION_SURVEY_ERROR"

  public static UPDATE_ORGANIZATION_SURVEY_TRIGGER = "UPDATE_ORGANIZATION_SURVEY_TRIGGER"
  public static UPDATE_ORGANIZATION_SURVEY_SUCCESS = "UPDATE_ORGANIZATION_SURVEY_SUCCESS"
  public static UPDATE_ORGANIZATION_SURVEY_ERROR = "UPDATE_ORGANIZATION_SURVEY_ERROR"

  public static DELETE_ORGANIZATION_SURVEY_TRIGGER = "DELETE_ORGANIZATION_SURVEY_TRIGGER"
  public static DELETE_ORGANIZATION_SURVEY_SUCCESS = "DELETE_ORGANIZATION_SURVEY_SUCCESS"
  public static DELETE_ORGANIZATION_SURVEY_ERROR = "DELETE_ORGANIZATION_SURVEY_ERROR"

  public static CLEAR_ORGANIZATION_SURVEYS = "CLEAR_ORGANIZATION_SURVEYS"

  public static fetchOrganizationSurveys: FetchOrganizationSurveys = (filter) => async (dispatch) => {
    dispatch({
      type: OrganizationSurveys.FETCH_ORGANIZATION_SURVEYS_TRIGGER,
    })

    try {
      const {
        data: { data },
        status,
      } = await API.get("/v2/organizationSurveys", {
        headers: AuthHelper.getAdminHeaders(),
        params: filter,
      })

      if (status) {
        dispatch({
          payload: { organizationSurveys: data },
          type: OrganizationSurveys.FETCH_ORGANIZATION_SURVEYS_SUCCESS,
        })
      }
      return
    } catch (err) {
      dispatch({
        payload: CommonHelper.getErrorMessage(err) || "Oops!... Looks like an error occurred!",
        type: OrganizationSurveys.FETCH_ORGANIZATION_SURVEYS_ERROR,
      })
    }
  }

  public static fetchOrganizationSurvey: FetchOrganizationSurvey = (id: string) => async (dispatch) => {
    dispatch({
      type: OrganizationSurveys.FETCH_ORGANIZATION_SURVEY_TRIGGER,
    })

    try {
      const {
        data: { data },
        status,
      } = await API.get(`/v2/organizationSurveys/${id}`, { headers: AuthHelper.getAdminHeaders() })

      if (status) {
        dispatch({
          payload: { organizationSurvey: data },
          type: OrganizationSurveys.FETCH_ORGANIZATION_SURVEY_SUCCESS,
        })
      }
      return
    } catch (err) {
      dispatch({
        payload: "Oops!... Looks like an error occurred!",
        type: OrganizationSurveys.FETCH_ORGANIZATION_SURVEY_ERROR,
      })
    }
  }

  public static fetchOrganizationSurveysByOrganizationId: FetchOrganizationSurveysByOrganizationId = (organizationId: string) => async (dispatch) => {
    dispatch({
      type: OrganizationSurveys.FETCH_ORGANIZATION_SURVEYS_TRIGGER,
    })

    try {
      const {
        data: { data },
        status,
      } = await API.get(`/v2/organizationSurveys/organization/${organizationId}`, {
        headers: AuthHelper.getAdminHeaders(),
      })

      if (status) {
        dispatch({
          payload: { organizationSurveys: data },
          type: OrganizationSurveys.FETCH_ORGANIZATION_SURVEYS_SUCCESS,
        })
      }
      return
    } catch (err) {
      dispatch({
        payload: "Oops!... Looks like an error occurred!",
        type: OrganizationSurveys.FETCH_ORGANIZATION_SURVEYS_ERROR,
      })
    }
  }

  public static fetchOrganizationSurveysByCallListQueueId: FetchOrganizationSurveysByCallListQueueId = (callListQueueId: string) => async (dispatch) => {
    dispatch({
      type: OrganizationSurveys.FETCH_ORGANIZATION_SURVEYS_TRIGGER,
    })

    try {
      const {
        data: { data },
        status,
      } = await API.get(`/v2/organizationSurveys/callListQueue/${callListQueueId}`, {
        headers: AuthHelper.getAdminHeaders(),
      })

      if (status) {
        dispatch({
          payload: { organizationSurveys: data },
          type: OrganizationSurveys.FETCH_ORGANIZATION_SURVEYS_SUCCESS,
        })
      }
      return
    } catch (err) {
      dispatch({
        payload: "Oops!... Looks like an error occurred!",
        type: OrganizationSurveys.FETCH_ORGANIZATION_SURVEYS_ERROR,
      })
    }
  }

  public static createOrganizationSurvey: CreateOrganizationSurvey = (values) => async (dispatch) => {
    dispatch({
      type: OrganizationSurveys.CREATE_ORGANIZATION_SURVEY_TRIGGER,
    })

    try {
      const {
        data: { data },
        status,
      } = await API.post("/v2/organizationSurveys", values, { headers: AuthHelper.getAdminHeaders() })

      if (status) {
        dispatch({
          payload: { organizationSurvey: data },
          type: OrganizationSurveys.CREATE_ORGANIZATION_SURVEY_SUCCESS,
        })
      }
      return
    } catch (err) {
      dispatch({
        payload: "Oops!... Looks like an error occurred!",
        type: OrganizationSurveys.CREATE_ORGANIZATION_SURVEY_ERROR,
      })
    }
  }

  public static updateOrganizationSurvey: UpdateOrganizationSurvey = (id: string, values: UpdateOrganizationSurveyParams) => async (dispatch) => {
    dispatch({
      type: OrganizationSurveys.UPDATE_ORGANIZATION_SURVEY_TRIGGER,
    })

    try {
      const {
        data: { data },
        status,
      } = await API.put(`/v2/organizationSurveys/${id}`, values, { headers: AuthHelper.getAdminHeaders() })

      if (status) {
        dispatch({
          payload: { organizationSurvey: data },
          type: OrganizationSurveys.UPDATE_ORGANIZATION_SURVEY_SUCCESS,
        })
      }
      return
    } catch (err) {
      dispatch({
        payload: "Oops!... Looks like an error occurred!",
        type: OrganizationSurveys.UPDATE_ORGANIZATION_SURVEY_ERROR,
      })
    }
  }

  public static deleteOrganizationSurvey: DeleteOrganizationSurvey = (id: string) => async (dispatch) => {
    dispatch({
      type: OrganizationSurveys.DELETE_ORGANIZATION_SURVEY_TRIGGER,
    })

    try {
      const {
        data: { data },
        status,
      } = await API.delete(`/v2/organizationSurveys/${id}`, { headers: AuthHelper.getAdminHeaders() })

      if (status) {
        dispatch({
          payload: { id },
          type: OrganizationSurveys.DELETE_ORGANIZATION_SURVEY_SUCCESS,
        })
      }
      return
    } catch (err) {
      dispatch({
        payload: "Oops!... Looks like an error occurred!",
        type: OrganizationSurveys.DELETE_ORGANIZATION_SURVEY_ERROR,
      })
    }
  }

  public static clearOrganizationSurveys: ClearOrganizationSurveys = () => ({
    type: OrganizationSurveys.CLEAR_ORGANIZATION_SURVEYS,
  })
}

export default OrganizationSurveys
